import { useEffect, useState } from "react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Award, Sparkles, Loader2, IndianRupee, ArrowUpRight, History as HistoryIcon } from "lucide-react";
import { toast } from "sonner";

const TIER_STYLE = {
  Bronze: { from: "from-amber-700", to: "to-orange-800", chip: "bg-amber-100 text-amber-800 border-amber-200" },
  Silver: { from: "from-slate-400", to: "to-slate-600", chip: "bg-slate-100 text-slate-700 border-slate-200" },
  Gold:   { from: "from-yellow-400", to: "to-amber-600", chip: "bg-yellow-100 text-yellow-800 border-yellow-200" },
};

export default function Loyalty() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeemPts, setRedeemPts] = useState(10);
  const [redeeming, setRedeeming] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/loyalty/me")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load loyalty status"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const redeem = async () => {
    if (!redeemPts || redeemPts % 10 !== 0) {
      toast.error("Redeem in multiples of 10 points");
      return;
    }
    setRedeeming(true);
    try {
      const { data: res } = await api.post("/loyalty/redeem", { points: redeemPts });
      toast.success(`Redeemed! INR ${res.credited_inr} credited to your wallet`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Redeem failed");
    } finally { setRedeeming(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" />
    </div>
  );

  const tier = data?.tier || "Bronze";
  const style = TIER_STYLE[tier] || TIER_STYLE.Bronze;
  const benefits = data?.tier_benefits || {};
  const progress = data?.orders_to_next_tier;
  const nextTier = data?.next_tier;
  const delivered = data?.delivered_count || 0;
  const milestoneTotal = nextTier === "Silver" ? 10 : (nextTier === "Gold" ? 50 : delivered);
  const pct = nextTier ? Math.min(100, Math.round((delivered / milestoneTotal) * 100)) : 100;

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="loyalty-page">
      <div className="animate-fadeInUp">
        <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900">Loyalty Rewards</h1>
        <p className="text-sm text-slate-500 mt-1">Earn points every time we deliver. Climb tiers for better perks.</p>
      </div>

      {/* Tier hero */}
      <div className={`bg-gradient-to-br ${style.from} ${style.to} rounded-2xl p-6 md:p-8 text-white shadow-lg animate-fadeInUp`} data-testid="loyalty-hero">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
            <Award className="h-6 w-6" />
          </div>
          <div>
            <p className="text-white/80 text-xs uppercase tracking-widest font-semibold">Current Tier</p>
            <p className="text-3xl font-extrabold" data-testid="loyalty-tier-label">{tier}</p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4">
          <div>
            <p className="text-white/70 text-xs">Points balance</p>
            <p className="text-2xl font-bold" data-testid="loyalty-points-balance">{data?.points_balance ?? 0}</p>
          </div>
          <div>
            <p className="text-white/70 text-xs">Delivered orders</p>
            <p className="text-2xl font-bold" data-testid="loyalty-delivered-count">{delivered}</p>
          </div>
        </div>

        {nextTier && progress !== null && (
          <div className="mt-6">
            <div className="flex items-center justify-between text-xs text-white/80 mb-1.5">
              <span>{progress > 0 ? `${progress} more orders to ${nextTier}` : `You're at ${nextTier} threshold!`}</span>
              <span>{delivered}/{milestoneTotal}</span>
            </div>
            <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-white rounded-full transition-all" style={{ width: `${pct}%` }} data-testid="loyalty-progress-bar" />
            </div>
          </div>
        )}
      </div>

      {/* Tier benefits */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 md:p-6 animate-fadeInUp" data-testid="loyalty-benefits">
        <h2 className="font-['Outfit'] text-base font-semibold text-slate-900 mb-4">Your perks</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          <PerkRow label="Earn rate" value={`${benefits.pts_per_100 || 1} pts / ₹100`} />
          <PerkRow label="Discount" value={benefits.discount_pct ? `${benefits.discount_pct}% off` : "—"} />
          <PerkRow label="Priority dispatch" value={benefits.priority ? "Yes" : "—"} />
        </div>
      </div>

      {/* Redeem */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 md:p-6 animate-fadeInUp" data-testid="loyalty-redeem-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center shrink-0">
            <IndianRupee className="h-5 w-5 text-[#FF6B00]" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-900">Redeem for wallet credit</p>
            <p className="text-xs text-slate-500 mt-0.5">10 points = ₹1. Redeem in multiples of 10.</p>
            <div className="flex items-center gap-2 mt-3">
              <input
                type="number"
                min="10"
                step="10"
                value={redeemPts}
                onChange={(e) => setRedeemPts(parseInt(e.target.value || "0", 10))}
                className="w-32 px-3 py-2 rounded-xl border border-slate-200 text-sm"
                data-testid="loyalty-redeem-input"
              />
              <Button onClick={redeem} disabled={redeeming || (data?.points_balance ?? 0) < redeemPts} className="bg-[#FF6B00] hover:bg-orange-600 text-white" data-testid="loyalty-redeem-btn">
                {redeeming ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1" /> Redeem</>}
              </Button>
              <span className="text-xs text-slate-500">= ₹{Math.floor(redeemPts / 10) || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* History */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 animate-fadeInUp" data-testid="loyalty-history">
        <div className="p-5 border-b border-slate-100 flex items-center gap-2">
          <HistoryIcon className="h-4 w-4 text-slate-400" />
          <h2 className="font-['Outfit'] text-base font-semibold text-slate-900">Recent activity</h2>
        </div>
        {!data?.history?.length ? (
          <div className="p-10 text-center">
            <Award className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No activity yet — get your first delivery to start earning.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-50">
            {[...data.history].reverse().map((h, idx) => (
              <li key={idx} className="px-5 py-3 flex items-center justify-between text-sm">
                <div>
                  <p className="font-medium text-slate-900">
                    {h.type === "earned" ? "Earned" : "Redeemed"}
                    {h.tracking_id ? ` · ${h.tracking_id}` : ""}
                  </p>
                  <p className="text-xs text-slate-400">{new Date(h.ts).toLocaleString()}</p>
                </div>
                <span className={`font-bold ${h.amount >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                  {h.amount >= 0 ? "+" : ""}{h.amount} pts
                  {h.credited_inr ? <ArrowUpRight className="inline h-3 w-3 ml-1" /> : null}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function PerkRow({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-100 p-3">
      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{label}</p>
      <p className="text-sm font-semibold text-slate-900 mt-1">{value}</p>
    </div>
  );
}
